// src/componentes/Comisiones/RecobrosPendientesBanner.jsx
//
// Banner para Dashboard: lista comisiones canceladas donde el comisionista
// ya cobró parte (o todo). Estado "pendiente": hay que decidir si
// recobrar (entrada de caja) o perdonar (asumir la pérdida).
//
// Se oculta si no hay nada pendiente.

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaExclamationCircle, FaArrowRight, FaUndo, FaHandHoldingHeart } from "react-icons/fa";
import {
  listarComisionesConRecobro,
  perdonarRecobro,
  registrarRecobro,
} from "../../services/comisionesRecobroService";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import notify from "../common/notify";
import { dialogService } from "../common/dialogService";
import "./RecobrosPendientesBanner.css";

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(Number(n || 0));

export default function RecobrosPendientesBanner() {
  const navigate = useNavigate();
  const { can } = useUserContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!can("comisiones.ver")) {
      setLoading(false);
      return;
    }
    try {
      const lista = await listarComisionesConRecobro({ estado: "pendiente" });
      setItems(lista);
    } catch (err) {
      console.warn("RecobrosPendientesBanner:", err);
    } finally {
      setLoading(false);
    }
  }, [can]);

  useEffect(() => { cargar(); }, [cargar]);

  const handlePerdonar = async (item) => {
    const motivo = await dialogService.prompt(
      `Perdonar el recobro de ${fmtMXN(item.recobroPendiente.monto)} a ${item.comisionistaNombre || "comisionista"}?\n\n` +
      `La empresa absorbe la pérdida. Esta acción queda registrada en auditoría.\n\nMotivo:`
    );
    if (motivo === null) return;
    try {
      await perdonarRecobro(item.id, motivo || "");
      notify.success("Recobro perdonado.");
      cargar();
    } catch (err) {
      notify.error(err?.message || "No se pudo perdonar.");
    }
  };

  const handleRecobrar = async (item) => {
    const restante =
      Number(item.recobroPendiente.monto || 0) -
      Number(item.recobroPendiente.montoRecuperado || 0);

    const montoStr = await dialogService.prompt(
      `Registrar pago del comisionista por recobro de comisión.\n\n` +
      `Pendiente: ${fmtMXN(restante)}\n\n` +
      `Monto recuperado (puede ser parcial):`,
      String(restante)
    );
    if (montoStr === null) return;

    const monto = Number(montoStr);
    if (!Number.isFinite(monto) || monto <= 0) {
      notify.error("Monto inválido.");
      return;
    }

    const referencia = await dialogService.prompt(
      "Referencia / método (efectivo, transferencia, etc.):",
      "efectivo"
    );
    if (referencia === null) return;

    const metodo = String(referencia).toLowerCase().includes("transfer")
      ? "transferencia"
      : "efectivo";

    try {
      await registrarRecobro(item.id, { monto, metodo, referencia, notas: "" });
      notify.success(
        monto >= restante
          ? "Recobro completado y caja actualizada."
          : "Pago parcial registrado."
      );
      cargar();
    } catch (err) {
      notify.error(err?.message || "No se pudo registrar el recobro.");
    }
  };

  if (loading || items.length === 0) return null;

  const totalPendiente = items.reduce(
    (acc, i) =>
      acc +
      Math.max(
        Number(i.recobroPendiente?.monto || 0) -
          Number(i.recobroPendiente?.montoRecuperado || 0),
        0
      ),
    0
  );

  return (
    <section className="recobros-banner">
      <header className="recobros-banner-head">
        <div>
          <FaExclamationCircle className="recobros-banner-icon" />
          <strong>
            {items.length} comisión{items.length === 1 ? "" : "es"} pagada
            {items.length === 1 ? "" : "s"} en deuda
            {items.length === 1 ? "" : "s"} cancelada
            {items.length === 1 ? "" : "s"}
          </strong>
          <span> · Total a decidir: {fmtMXN(totalPendiente)}</span>
        </div>
      </header>

      <ul className="recobros-banner-list">
        {items.slice(0, 5).map((item) => {
          const pend =
            Number(item.recobroPendiente?.monto || 0) -
            Number(item.recobroPendiente?.montoRecuperado || 0);
          return (
            <li key={item.id} className="recobros-banner-item">
              <div className="recobros-banner-item-info">
                <strong>{item.comisionistaNombre || "Sin nombre"}</strong>
                <small>
                  {item.propiedadFolio || "—"} ·{" "}
                  {fmtMXN(pend)} pendiente
                  {item.recobroPendiente?.montoRecuperado > 0 && (
                    <> · {fmtMXN(item.recobroPendiente.montoRecuperado)} ya recuperado</>
                  )}
                </small>
              </div>
              <div className="recobros-banner-actions">
                <button
                  className="btn-recobro btn-perdon"
                  onClick={() => handlePerdonar(item)}
                  title="Absorber la pérdida — el comisionista no devuelve"
                >
                  <FaHandHoldingHeart /> Perdonar
                </button>
                <button
                  className="btn-recobro btn-recobrar"
                  onClick={() => handleRecobrar(item)}
                  title="Registrar pago del comisionista"
                >
                  <FaUndo /> Recobrar
                </button>
                <button
                  className="btn-recobro btn-ver"
                  onClick={() => navigate(`/app/comisiones/${item.id}`)}
                  title="Ver detalle de comisión"
                >
                  <FaArrowRight />
                </button>
              </div>
            </li>
          );
        })}
        {items.length > 5 && (
          <li className="recobros-banner-more">
            +{items.length - 5} más en /app/comisiones
          </li>
        )}
      </ul>
    </section>
  );
}
